import { useState, type ReactNode } from "react";
import { Download, Pencil, RotateCcw, TriangleAlert } from "lucide-react";
import { Button } from "@/components/form/Button";
import { queue } from "@/components/Toast";
import { cn } from "@/utils/helpers";
import type { PostViewport } from "../components/StatusBanner";
import { timeline } from "./liveModel";
import { LIVE_ACCEPT, type LiveStageHandle } from "./liveStage";
import LiveNumbers from "./LiveNumbers";
import LivePostDialog from "./LivePostDialog";
import LiveResults from "./LiveResults";
import LiveTimeline from "./LiveTimeline";
import SlidesEditor from "./SlidesEditor";
import LiveWorkspace from "./LiveWorkspace";
import { downloadKit, kitDownloads } from "./downloadKit";
import "./live.css";

/** A skeleton shaped like the tab: the files row, then three posts. */
export function LiveSkeleton({ viewport }: { viewport: PostViewport }) {
  return (
    <div role="status" aria-label="Loading the files and captions" className={cn("flex flex-col", viewport === "phone" ? "gap-6" : "gap-9", viewport === "desktop" && "pt-9")}>
      <div className="h-[290px] w-full animate-pulse rounded-panel bg-co-surface motion-reduce:animate-none" />
      <div className="flex gap-5 overflow-hidden">
        {[0, 1, 2].map((index) => (
          <div key={index} className="h-[498px] w-[280px] shrink-0 animate-pulse rounded-card bg-co-surface motion-reduce:animate-none" />
        ))}
      </div>
    </div>
  );
}

/**
 * The Live tab (DESIGN.md "Live"): the files come in, M7 matches each to a
 * channel from the brief, the team checks and edits them in one editor per
 * post, the kit goes to the client, and once the posts are found live the tab
 * turns into their first 24 hours. The banner above says whose turn it is; this
 * is the work under it.
 */
export default function LiveView({
  stage,
  viewport,
  clientName,
  canEdit,
  downloadAll = false,
  downloadName = "Kit",
  comments,
  onEditPlatforms,
  slotId,
}: {
  /** The post's id: the numbers under the posts once one is live, and each live post's numbers from its "Live since". */
  slotId?: string;
  /** Opens the post's platform picker (team only). */
  onEditPlatforms?: () => void;
  /** The audience's comments on the live posts, behind the numbers' Comments switch. */
  comments?: (postCount: number, desk: boolean) => ReactNode;
  stage: LiveStageHandle;
  viewport: PostViewport;
  clientName: string;
  canEdit: boolean;
  /** The client's Kit: a Download all button for every file of the post. */
  downloadAll?: boolean;
  /** The zip's name (the post title). */
  downloadName?: string;
  /** Which post opens in the editor (the harness's editing states). */
  initialEditing?: string | null;
}) {
  const { data, registerInput, timeZone, uploads } = stage;
  // The slide the static post's pictures show: the one picked under them.
  const [shownSlide, setShownSlide] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [numbersOf, setNumbersOf] = useState<string | null>(null);
  const phone = viewport === "phone";
  const desk = viewport === "desktop";
  const files = new Map(data.files.map((file) => [file.id, file]));

  if (stage.loading) return <LiveSkeleton viewport={viewport} />;
  if (stage.error) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 px-4 py-12 text-center">
        <span className="grid size-11 place-items-center rounded-card bg-[var(--co-tonal)] text-co-alert-ink">
          <TriangleAlert className="size-5" strokeWidth={1.5} aria-hidden="true" />
        </span>
        <h2 className="font-section text-[22px] font-extrabold leading-tight text-co-text">The files could not load</h2>
        <p className="max-w-[420px] text-[14px] leading-relaxed text-co-muted">Nothing was changed. Try again in a moment.</p>
        {stage.retry ? (
          <Button size="small" variant="secondary" icon={RotateCcw} className="mt-2" onPress={stage.retry}>
            Try again
          </Button>
        ) : null}
      </div>
    );
  }

  const livePosts = data.posts.filter((post) => post.liveSince);
  // Refresh reads every live channel CreativeOS reads numbers for (edit roles: a read can be a paid one).
  const refreshAll =
    canEdit && stage.refreshNumbers
      ? () => {
          for (const post of livePosts) if (post.tracking) stage.refreshNumbers!(post.channelId);
        }
      : undefined;
  const numbers =
    slotId && livePosts.length ? (
      <LiveNumbers
        slotId={slotId}
        data={data}
        viewport={viewport}
        timeZone={timeZone}
        comments={comments ? comments(livePosts.length, desk) : undefined}
        onRefresh={refreshAll}
      />
    ) : null;
  const openLive = slotId ? (channelId: string) => setNumbersOf(channelId) : undefined;
  const numbersChannel = numbersOf ? data.channels.find((channel) => channel.id === numbersOf) : undefined;
  const numbersPost = numbersOf ? data.posts.find((post) => post.channelId === numbersOf) : undefined;
  const dialog =
    slotId && numbersChannel && numbersPost?.liveSince ? (
      <LivePostDialog
        slotId={slotId}
        data={data}
        channel={numbersChannel}
        post={numbersPost}
        timeZone={timeZone}
        onClose={() => setNumbersOf(null)}
        onRefresh={canEdit && stage.refreshNumbers && numbersPost.tracking ? () => stage.refreshNumbers!(numbersChannel.id) : undefined}
        onSetLink={canEdit && stage.setPostLink ? (url) => void Promise.resolve(stage.setPostLink!(numbersChannel.id, url)).catch(() => undefined) : undefined}
      />
    ) : null;

  const downloadable = downloadAll ? kitDownloads(data.files) : [];
  const downloadBar =
    downloadable.length > 0 || onEditPlatforms ? (
      <div className="mb-6 flex justify-end gap-2">
        {onEditPlatforms ? (
          <Button size="small" variant="secondary" icon={Pencil} iconClassName="size-3.5" onPress={onEditPlatforms}>
            Edit platforms
          </Button>
        ) : null}
        {downloadable.length > 0 ? (
          <Button
            size="small"
            variant="secondary"
            icon={Download}
            iconPosition="right"
            iconClassName="size-3.5"
            loading={downloading}
            isDisabled={downloading}
            onPress={async () => {
              setDownloading(true);
              try {
                const result = await downloadKit(data.files, downloadName);
                if (result.separately > 0) {
                  queue.add({
                    title: result.saved > 0 ? "Some files download on their own" : "The files download one by one",
                    description: "Your browser may ask to allow several downloads, or open a file in a new tab.",
                  });
                }
              } catch {
                queue.add({ title: "The files could not be downloaded", description: "Try again in a moment.", variant: "error" });
              } finally {
                setDownloading(false);
              }
            }}
          >
            {downloadable.length === 1 ? "Download" : `Download all (${downloadable.length})`}
          </Button>
        ) : null}
      </div>
    ) : null;

  const input = (
    <input
      ref={registerInput}
      type="file"
      multiple
      accept={stage.accept ?? LIVE_ACCEPT}
      className="hidden"
      aria-hidden="true"
      tabIndex={-1}
      onChange={(event) => {
        const picked = Array.from(event.target.files ?? []);
        event.target.value = "";
        if (picked.length) stage.addFiles?.(picked);
      }}
    />
  );

  const steps = phone ? null : timeline(data, { clientName, timeZone: timeZone, uploadsInFlight: uploads.inFlight, uploadTotal: uploads.total, compact: viewport === "tablet" });

  // Every channel out (live, or never posted for want of an account): the
  // cards flip to their numbers and replace the work (Figma 08, 177:65940).
  const settled = data.posts.length > 0 && data.posts.every((post) => post.liveSince || post.route?.state === "connect");
  if (data.results || (livePosts.length > 0 && settled)) {
    return (
      <>
        {input}
        {downloadBar}
        <div className={cn("flex flex-col", desk ? "pt-6" : phone ? undefined : "pt-5")}>
          <LiveResults data={data} viewport={viewport} timeZone={timeZone} numbers={numbers} onOpenLive={openLive} />
          {steps ? (
            <div className="mt-7">
              <LiveTimeline steps={steps} />
            </div>
          ) : null}
        </div>
        {dialog}
      </>
    );
  }

  // A static post's pictures: one, or a carousel (the post owns them), under the files.
  const slidesRow =
    data.slides && canEdit ? (
      <SlidesEditor
        slides={data.slides}
        channels={data.channels}
        files={files}
        stage={stage}
        canEdit={canEdit}
        current={shownSlide}
        onPick={setShownSlide}
      />
    ) : null;

  return (
    // Figma 08, 6 Live: the work 20 under the banner, the timeline at the foot.
    <section aria-label="Live" className={cn("relative flex flex-col", desk && "pt-5")}>
      {input}
      {downloadBar}
      <LiveWorkspace stage={stage} viewport={viewport} clientName={clientName} canEdit={canEdit} below={slidesRow} />
      {numbers ? <div className={phone ? "mt-6" : "mt-10"}>{numbers}</div> : null}
      {steps ? (
        <div className={desk ? "mt-10" : "mt-7"}>
          <LiveTimeline steps={steps} />
        </div>
      ) : null}
      {dialog}
    </section>
  );
}
